import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight, LayoutDashboard, Settings, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import LogoIcon from './LogoIcon';

const LandingNavbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Product', href: '/#features', type: 'scroll' },
    { name: 'Pricing', href: '/pricing', type: 'route' },
    { name: 'Guide', href: '/guide', type: 'route' },
    ...(user ? [
      { name: 'Dashboard', href: '/dashboard', type: 'route' },
      { name: 'Analytics', href: '/analytics', type: 'route' }
    ] : [])
  ];

  const handleNavClick = (link) => {
    if (link.type === 'route') {
      navigate(link.href);
    } else {
      if (location.pathname === '/') {
        const id = link.href.split('#')[1];
        const element = document.getElementById(id);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate(link.href);
      }
    }
    setMobileMenuOpen(false);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/90 backdrop-blur-md border-b border-[#E5E7EB] py-3.5 shadow-[0_2px_15px_rgba(0,0,0,0.03)]'
            : 'bg-transparent py-6'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-14">
            {/* Brand Logo */}
            <div
              onClick={() => navigate('/')}
              className="flex items-center gap-3 cursor-pointer group"
            >
              <LogoIcon className="h-8 w-auto text-[#111827]" />
              <span className="font-semibold text-lg tracking-tight text-[#111827]">
                AI Digital Twin
              </span>
            </div>

            {/* Navigation links */}
            <nav className="hidden lg:flex items-center gap-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-full px-5 py-2">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.href || (location.pathname === '/' && link.type === 'scroll');
                return (
                  <button
                    key={link.name}
                    onClick={() => handleNavClick(link)}
                    className={`px-4 py-1.5 text-xs font-medium rounded-full transition-all ${
                      isActive
                        ? 'text-[#111827] bg-white shadow-2xs font-semibold'
                        : 'text-[#4B5563] hover:text-[#111827] hover:bg-white/60'
                    }`}
                  >
                    {link.name}
                  </button>
                );
              })}
            </nav>

            {/* Auth / Account buttons */}
            <div className="hidden lg:flex items-center gap-3">
              {loading ? (
                <div className="h-9 w-24 bg-[#F3F4F6] animate-pulse rounded-lg" />
              ) : user ? (
                <div className="relative">
                  <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className="flex items-center gap-2 focus:outline-none"
                  >
                    {user.profile_picture ? (
                      <img
                        src={user.profile_picture}
                        alt={user.full_name || "Profile"}
                        className="w-9 h-9 rounded-full object-cover border border-[#E5E7EB]"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-[#111827] text-white flex items-center justify-center font-bold text-xs">
                        {user.full_name ? user.full_name.charAt(0).toUpperCase() : (user.email ? user.email.charAt(0).toUpperCase() : 'U')}
                      </div>
                    )}
                  </button>

                  <AnimatePresence>
                    {dropdownOpen && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setDropdownOpen(false)} />
                        <motion.div
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 8 }}
                          transition={{ duration: 0.15 }}
                          className="absolute right-0 mt-2 w-56 bg-white border border-[#E5E7EB] rounded-xl p-3 shadow-lg z-20 flex flex-col gap-2"
                        >
                          <div className="flex flex-col border-b border-[#E5E7EB] pb-2">
                            <span className="text-[10px] text-slate-500 font-bold tracking-wider uppercase">Account</span>
                            <span className="text-xs font-semibold text-[#111827] truncate mt-0.5">{user.full_name || 'User'}</span>
                            <span className="text-[11px] text-slate-500 truncate">{user.email}</span>
                          </div>
                          <div className="flex flex-col gap-0.5">
                            <button
                              onClick={() => { setDropdownOpen(false); navigate('/dashboard'); }}
                              className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs text-[#374151] hover:bg-[#F3F4F6] text-left font-medium"
                            >
                              <LayoutDashboard className="w-3.5 h-3.5 text-slate-700" />
                              Dashboard
                            </button>
                            <button
                              onClick={() => { setDropdownOpen(false); navigate('/settings'); }}
                              className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs text-[#374151] hover:bg-[#F3F4F6] text-left font-medium"
                            >
                              <Settings className="w-3.5 h-3.5 text-slate-500" />
                              Settings
                            </button>
                            <button
                              onClick={() => { setDropdownOpen(false); logout(); navigate('/'); }}
                              className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs text-rose-600 hover:bg-rose-50 text-left font-medium"
                            >
                              <LogOut className="w-3.5 h-3.5" />
                              Logout
                            </button>
                          </div>
                        </motion.div>
                      </>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <>
                  <button
                    onClick={() => navigate('/login')}
                    className="px-4 py-2 text-xs font-medium text-[#374151] hover:text-[#111827] hover:bg-[#F3F4F6] transition-colors rounded-lg"
                  >
                    Sign in
                  </button>
                  <button
                    onClick={() => navigate('/register')}
                    className="px-5 py-2.5 bg-[#111827] hover:bg-[#1F2937] text-white rounded-lg text-xs font-semibold shadow-xs transition-colors flex items-center gap-2"
                  >
                    Get Started
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden text-[#111827] p-2 rounded-lg hover:bg-[#F3F4F6]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-white pt-24 px-6 lg:hidden overflow-y-auto"
          >
            <div className="flex flex-col gap-3">
              {navLinks.map((link) => (
                <button
                  key={link.name}
                  onClick={() => handleNavClick(link)}
                  className="text-base font-medium py-3 text-left px-4 rounded-xl text-[#111827] hover:bg-[#F3F4F6]"
                >
                  {link.name}
                </button>
              ))}
              <div className="pt-4 border-t border-[#E5E7EB] flex flex-col gap-3 mt-2">
                {!user ? (
                  <>
                    <button
                      onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                      className="w-full py-3 text-sm font-medium text-[#111827] bg-[#F3F4F6] rounded-xl"
                    >
                      Sign in
                    </button>
                    <button
                      onClick={() => { navigate('/register'); setMobileMenuOpen(false); }}
                      className="w-full py-3 bg-[#111827] text-white rounded-xl text-sm font-semibold shadow-xs"
                    >
                      Get Started Free
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}
                    className="w-full py-3 bg-[#111827] text-white rounded-xl text-sm font-semibold shadow-xs"
                  >
                    Go to Dashboard
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default LandingNavbar;
